import React, { useState } from 'react';
import { ImageIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dropzone, DropzoneEmptyState } from 'src/components/ui/shadcn-io/dropzone/index';

interface ImageUploadProps {
  onFilesSelected: (files: File[]) => void;
  maxFiles?: number;
  disabled?: boolean;
  className?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onFilesSelected,
  maxFiles = 10,
  disabled = false,
  className,
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleDrop = (acceptedFiles: File[]) => {
    setError(null);
    onFilesSelected(acceptedFiles);
  };

  const handleError = (error: Error) => {
    setError(error.message);
  };

  return (
    <div className={cn('w-full space-y-2', className)}>
      <Dropzone
        maxFiles={maxFiles}
        maxSize={10 * 1024 * 1024} // 10MB
        accept={{
          'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.heic', '.heif'],
        }}
        onDrop={handleDrop}
        onError={handleError}
        disabled={disabled}
        className={cn(
          'border-dashed border-2 hover:border-primary/50 transition-colors',
          error && 'border-destructive',
        )}
      >
        <DropzoneEmptyState>
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="rounded-full p-4 bg-slate-200">
              <ImageIcon className="h-8 w-8 text-muted" />
            </div>

            <div className="space-y-1 text-center">
              <p className="text-sm font-medium text-muted">Upload images</p>
              <p className="text-xs text-muted">Drag and drop or click to upload</p>
              <p className="text-xs text-muted">
                PNG, JPG, GIF, WebP up to 10MB (max {maxFiles} files)
              </p>
            </div>
          </div>
        </DropzoneEmptyState>
      </Dropzone>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
